import React from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { post } from '../../utils/api';
import { CloseX, ignoreBackdropEscapeClose } from './portalUi';

function isValidEmail(value) {
  const v = String(value || '').trim();
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v);
}

export default function NewsletterSubscribeDialog({
  open,
  initialEmail = '',
  onClose,
  onSuccess,
}) {
  const [email, setEmail] = React.useState(String(initialEmail || '').trim());
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState('');
  const [status, setStatus] = React.useState({ type: '', text: '' });

  React.useEffect(() => {
    if (!open) return;
    setEmail(String(initialEmail || '').trim());
    setSaving(false);
    setError('');
    setStatus({ type: '', text: '' });
  }, [open, initialEmail]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const cleanEmail = String(email || '').trim().toLowerCase();
    if (!isValidEmail(cleanEmail)) {
      setError('Please enter a valid email address.');
      return;
    }

    setSaving(true);
    setError('');
    setStatus({ type: '', text: '' });
    try {
      const out = await post('/portal/me/newsletter/subscribe', { email: cleanEmail });
      const newsletter = out?.newsletter || {};
      const data = out?.data && typeof out.data === 'object' ? out.data : null;
      const statusText =
        newsletter.status === 'already_subscribed'
          ? `This email is already subscribed: ${newsletter.email || cleanEmail}`
          : `Subscribed successfully: ${newsletter.email || cleanEmail}`;
      setStatus({ type: 'success', text: statusText });
      onSuccess?.({ data, newsletter, email: cleanEmail });
    } catch (e2) {
      const msg = String(e2?.message || 'Could not subscribe to newsletter.');
      setStatus({ type: 'error', text: msg });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(e, reason) => {
        if (ignoreBackdropEscapeClose(e, reason)) return;
        onClose?.();
      }}
      disableEscapeKeyDown
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
        <Typography variant="h6" component="span">
          Newsletter subscription
        </Typography>
        <CloseX onClick={() => onClose?.()} disabled={saving} />
      </DialogTitle>
      <DialogContent dividers>
        <Box component="form" onSubmit={handleSubmit}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Subscribe to temple updates, events, and announcements. We prefilled the email on your devotee profile;
            update it here if needed, then continue.
          </Typography>
          <TextField
            label="Email address"
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setError('');
            }}
            fullWidth
            size="small"
            autoFocus
            error={!!error}
            helperText={error || ' '}
            sx={{ mb: 1 }}
          />
          {status.text ? (
            <Alert severity={status.type === 'error' ? 'error' : 'success'} sx={{ mb: 1.5 }}>
              {status.text}
            </Alert>
          ) : null}
          <Stack direction="row" justifyContent="flex-end" spacing={1}>
            <Button variant="outlined" onClick={() => onClose?.()} disabled={saving}>
              {status.type ? 'Close' : 'Cancel'}
            </Button>
            {!status.type ? (
              <Button type="submit" variant="contained" disabled={saving}>
                {saving ? <CircularProgress size={20} color="inherit" /> : 'Continue'}
              </Button>
            ) : null}
          </Stack>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
